const TRACKED_EVENTS = new Set([
  'view_item_list',
  'select_item',
  'view_item',
  'add_to_cart',
  'add_to_wishlist',
  'view_cart',
  'remove_from_cart',
]);

const STORAGE_KEY = 'amoi:ecommerce:item-context:v1';
const config = window.AmoiEcommerceConfig || {};
const currency = config.currency || 'UAH';
const observedCards = new WeakSet();
const impressedCards = new WeakSet();
const visibleItemsByList = new WeakMap();
const boundCartDrawers = new WeakSet();
let cartState = Array.isArray(config.cartItems) ? config.cartItems : [];
let activeCoupon = cleanString(config.coupon);

/** @param {unknown} value */
function toNumber(value) {
  const number = typeof value === 'number' ? value : Number.parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(number) ? Number(number.toFixed(2)) : undefined;
}

/** @param {unknown} value */
function cleanString(value) {
  if (value === null || value === undefined) return undefined;
  const string = String(value).trim();
  return string && string !== 'undefined' && string !== 'null' ? string : undefined;
}

/** @param {Record<string, unknown>} rawItem */
export function normalizeItem(rawItem) {
  const item = {
    item_name: cleanString(rawItem.item_name),
    item_id: cleanString(rawItem.item_id),
    price: toNumber(rawItem.price),
    coupon: cleanString(rawItem.coupon),
    in_stoke: cleanString(rawItem.in_stoke),
    item_brand: 'AMOI',
    item_category: cleanString(rawItem.item_category),
    item_category2: cleanString(rawItem.item_category2),
    item_category3: cleanString(rawItem.item_category3),
    item_category4: cleanString(rawItem.item_category4),
    item_variant: cleanString(rawItem.item_variant),
    item_size: cleanString(rawItem.item_size),
    item_list_name: cleanString(rawItem.item_list_name),
    item_list_id: cleanString(rawItem.item_list_id),
    google_business_vertical: cleanString(rawItem.google_business_vertical) || 'retail',
    index: Number.isFinite(Number(rawItem.index)) ? Number(rawItem.index) : undefined,
    quantity: Number.isFinite(Number(rawItem.quantity)) ? Number(rawItem.quantity) : 1,
  };

  const discount = toNumber(rawItem.discount);
  if (discount !== undefined && discount > 0) item.discount = discount;

  return Object.fromEntries(Object.entries(item).filter(([, value]) => value !== undefined && value !== ''));
}

function readContexts() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * @param {Record<string, unknown>} item
 * @param {{productId?: unknown, variantId?: unknown}} identifiers
 */
function saveContext(item, identifiers = {}) {
  const normalized = normalizeItem(item);
  delete normalized.coupon;
  const contexts = readContexts();
  const keys = [
    cleanString(identifiers.productId) && `product:${cleanString(identifiers.productId)}`,
    cleanString(identifiers.variantId) && `variant:${cleanString(identifiers.variantId)}`,
    normalized.item_id && `sku:${normalized.item_id}`,
  ].filter(Boolean);

  for (const key of keys) contexts[key] = normalized;

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(contexts));
  } catch {
    // Tracking must never break storefront interactions when storage is unavailable.
  }
}

/** @param {{productId?: unknown, variantId?: unknown, itemId?: unknown}} identifiers */
function findContext(identifiers = {}) {
  const contexts = readContexts();
  const keys = [
    cleanString(identifiers.variantId) && `variant:${cleanString(identifiers.variantId)}`,
    cleanString(identifiers.productId) && `product:${cleanString(identifiers.productId)}`,
    cleanString(identifiers.itemId) && `sku:${cleanString(identifiers.itemId)}`,
  ].filter(Boolean);

  for (const key of keys) {
    if (contexts[key]) return contexts[key];
  }
  return {};
}

/**
 * Pushes the canonical two-message ecommerce sequence expected by GTM.
 * @param {string} eventName
 * @param {Record<string, unknown>} ecommerce
 */
export function pushEvent(eventName, ecommerce) {
  if (!TRACKED_EVENTS.has(eventName)) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event: eventName, ecommerce });

  if (window.Shopify?.designMode || ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    console.log(`[AMOI dataLayer] ${eventName} ${JSON.stringify(ecommerce)}`);
  }
}

/** @param {HTMLElement} card */
function getCardList(card) {
  const listNameOwner = card.closest('[data-gtm-list-name]');
  const listRoot = card.closest('[data-testid="product-grid"], .resource-list, [data-gtm-list-name]') || document.body;
  const listName = cleanString(card.dataset.itemListName) || cleanString(listNameOwner?.dataset.gtmListName) || 'Продукти';
  const cards = Array.from(listRoot.querySelectorAll('.js-gtm-product-card'));
  const index = Math.max(1, cards.indexOf(card) + 1);
  return { listRoot, listName, index };
}

/** @param {HTMLElement} card */
export function itemFromCard(card) {
  const { listName, index } = getCardList(card);
  const item = normalizeItem({
    item_name: card.dataset.itemName,
    item_id: card.dataset.itemId,
    price: toNumber(card.dataset.itemPriceCents) / 100,
    discount: toNumber(card.dataset.itemDiscountCents) / 100,
    in_stoke: card.dataset.inStock,
    item_brand: card.dataset.itemBrand,
    item_category: card.dataset.itemCategory,
    item_category2: card.dataset.itemCategory2,
    item_variant: card.dataset.itemVariant,
    item_size: card.dataset.itemSize,
    item_list_name: listName,
    item_list_id: listName,
    google_business_vertical: 'retail',
    index,
    quantity: 1,
  });

  saveContext(item, { productId: card.dataset.productId, variantId: card.dataset.variantId });
  return item;
}

function getSelectedPdpVariantId(root) {
  return cleanString(root.querySelector('form[action*="/cart/add"] [name="id"]')?.value) || cleanString(root.dataset.variantId);
}

/** @param {HTMLElement} root */
export function itemFromProductPage(root) {
  const variantId = getSelectedPdpVariantId(root);
  const productConfig = config.currentProduct || {};
  const variant = Array.isArray(productConfig.variants)
    ? productConfig.variants.find((entry) => String(entry.variant_id) === String(variantId)) || {}
    : {};
  const identifiers = {
    productId: root.dataset.productId || productConfig.product_id,
    variantId,
    itemId: variant.item_id || root.dataset.itemId,
  };
  const origin = findContext(identifiers);
  const item = normalizeItem({
    ...origin,
    item_name: productConfig.item_name || root.dataset.itemName,
    item_id: variant.item_id || root.dataset.itemId,
    price: variant.price ?? toNumber(root.dataset.itemPriceCents) / 100,
    discount: variant.discount ?? toNumber(root.dataset.itemDiscountCents) / 100,
    in_stoke: variant.in_stoke || root.dataset.inStock,
    item_brand: productConfig.item_brand || root.dataset.itemBrand,
    item_category: productConfig.item_category || root.dataset.itemCategory,
    item_category2: productConfig.item_category2 || root.dataset.itemCategory2,
    item_variant: variant.item_variant || productConfig.item_variant || root.dataset.itemVariant,
    item_size: variant.item_size || root.dataset.itemSize,
    item_list_name: origin.item_list_name || 'Product page',
    item_list_id: origin.item_list_id || 'Product page',
    google_business_vertical: 'retail',
    quantity: 1,
  });

  saveContext(item, identifiers);
  return item;
}

export function trackViewItem() {
  if (!config.currentProduct) return;
  const root = document.querySelector('[data-testid="product-information"][data-gtm-product]');
  if (!(root instanceof HTMLElement)) return;

  const item = itemFromProductPage(root);
  pushEvent('view_item', {
    currency,
    value: item.price,
    items: [item],
  });
}

/** @param {HTMLElement} card */
function rememberVisibleCard(card) {
  const { listRoot } = getCardList(card);
  const item = itemFromCard(card);
  const items = visibleItemsByList.get(listRoot) || new Map();

  items.set(card, item);
  visibleItemsByList.set(listRoot, items);
  impressedCards.add(card);
  cardObserver?.unobserve(card);

  return { item, listRoot };
}

/** @param {HTMLElement} listRoot */
function getVisibleListItems(listRoot) {
  return Array.from(visibleItemsByList.get(listRoot)?.values() || [])
    .sort((first, second) => (first.index || 0) - (second.index || 0));
}

/** @param {IntersectionObserverEntry[]} entries */
function handleVisibleCards(entries) {
  for (const entry of entries) {
    if (!entry.isIntersecting || !(entry.target instanceof HTMLElement) || impressedCards.has(entry.target)) continue;
    rememberVisibleCard(entry.target);
  }
}

const cardObserver = typeof IntersectionObserver === 'function'
  ? new IntersectionObserver(handleVisibleCards, { threshold: 0.2 })
  : null;

function observeProductCards() {
  for (const card of document.querySelectorAll('.js-gtm-product-card')) {
    if (!(card instanceof HTMLElement) || observedCards.has(card)) continue;
    observedCards.add(card);
    if (cardObserver) cardObserver.observe(card);
  }
}

/** @param {MouseEvent} event */
function handleProductSelection(event) {
  if (!(event.target instanceof Element)) return;
  const card = event.target.closest('.js-gtm-product-card');
  if (!(card instanceof HTMLElement)) return;
  if (event.target.closest('button, ooo-wl-wishlist-button, .quick-add, quick-add-component')) {
    itemFromCard(card);
    return;
  }

  const link = event.target.closest('a[href*="/products/"]');
  if (!(link instanceof HTMLAnchorElement) || event.defaultPrevented) return;

  const { item, listRoot } = rememberVisibleCard(card);
  pushEvent('view_item_list', { currency, items: getVisibleListItems(listRoot) });
  pushEvent('select_item', { currency, items: [item] });
}

/** @param {MouseEvent} event */
function handleWishlist(event) {
  if (!(event.target instanceof Element)) return;
  const wishlist = event.target.closest('ooo-wl-wishlist-button');
  if (!(wishlist instanceof HTMLElement)) return;

  const stateHolder = wishlist.querySelector('[aria-checked]') || wishlist;
  if (stateHolder.getAttribute('aria-checked') === 'true') return;

  const card = wishlist.closest('.js-gtm-product-card');
  const productRoot = wishlist.closest('[data-testid="product-information"][data-gtm-product]');
  const item = card instanceof HTMLElement
    ? itemFromCard(card)
    : productRoot instanceof HTMLElement
      ? itemFromProductPage(productRoot)
      : null;
  if (!item) return;

  pushEvent('add_to_wishlist', {
    currency,
    value: Number(((item.price || 0) * (item.quantity || 1)).toFixed(2)),
    items: [item],
  });
}

function initialCartFallback(line) {
  return config.cartItems?.find((entry) =>
    (line.key && String(entry.key) === String(line.key)) ||
    (line.variant_id && String(entry.variant_id) === String(line.variant_id))
  )?.item || {};
}

/**
 * @param {Record<string, any>} line
 * @param {number} quantity
 * @param {number} fallbackIndex
 */
export function itemFromCartLine(line, quantity = line.quantity, fallbackIndex = 1) {
  const configuredItem = line.item || initialCartFallback(line);
  const productVariant = config.currentProduct?.variants?.find((entry) => String(entry.variant_id) === String(line.variant_id)) || {};
  const sku = cleanString(line.sku) || productVariant.item_id || configuredItem.item_id;
  const origin = findContext({ productId: line.product_id, variantId: line.variant_id, itemId: sku });
  const price = line.final_price !== undefined ? toNumber(line.final_price / 100) : configuredItem.price;
  const lineDiscount = line.original_price > line.final_price ? toNumber((line.original_price - line.final_price) / 100) : undefined;
  const currentSize = !String(line.variant_title || '').includes('Default') ? line.variant_title : undefined;
  const lineCoupon = line.discount_allocations?.find((allocation) => allocation.discount_application)?.discount_application?.title;
  const item = normalizeItem({
    ...configuredItem,
    ...origin,
    item_name: line.product_title || line.title || configuredItem.item_name,
    item_id: sku,
    price,
    discount: lineDiscount || productVariant.discount || origin.discount || configuredItem.discount,
    coupon: lineCoupon || origin.coupon || configuredItem.coupon,
    in_stoke: productVariant.in_stoke || origin.in_stoke || configuredItem.in_stoke || 'true',
    item_brand: line.vendor || configuredItem.item_brand,
    item_category: origin.item_category || configuredItem.item_category || 'Усі товари',
    item_category2: origin.item_category2 || configuredItem.item_category2,
    item_variant: productVariant.item_variant || origin.item_variant || configuredItem.item_variant,
    item_size: productVariant.item_size || origin.item_size || configuredItem.item_size || currentSize,
    item_list_name: origin.item_list_name || configuredItem.item_list_name || 'In cart list',
    item_list_id: origin.item_list_id || configuredItem.item_list_id || 'In cart list',
    index: origin.index || configuredItem.index || fallbackIndex,
    google_business_vertical: 'retail',
    quantity,
  });

  saveContext(item, { productId: line.product_id, variantId: line.variant_id });
  return item;
}

function stableProperties(properties) {
  if (!properties || typeof properties !== 'object') return '';
  return JSON.stringify(Object.entries(properties).sort(([left], [right]) => left.localeCompare(right)));
}

function lineIdentity(line) {
  const variantId = cleanString(line.variant_id || line.id) || 'unknown';
  const sellingPlanId = cleanString(line.selling_plan_id || line.selling_plan_allocation?.selling_plan?.id) || '';
  return `${variantId}:${sellingPlanId}:${stableProperties(line.properties)}`;
}

/** @param {Record<string, any>[]} before @param {Record<string, any>[]} after */
export function trackCartDiff(before, after, coupon = activeCoupon) {
  const beforeMap = new Map(before.map((line) => [lineIdentity(line), line]));
  const afterMap = new Map(after.map((line) => [lineIdentity(line), line]));
  const added = [];
  const removed = [];

  for (const [key, line] of afterMap) {
    const oldQuantity = Number(beforeMap.get(key)?.quantity || 0);
    const delta = Number(line.quantity || 0) - oldQuantity;
    if (delta > 0) added.push(itemFromCartLine(line, delta, after.indexOf(line) + 1));
  }

  for (const [key, line] of beforeMap) {
    const newQuantity = Number(afterMap.get(key)?.quantity || 0);
    const delta = Number(line.quantity || 0) - newQuantity;
    if (delta > 0) removed.push(itemFromCartLine(line, delta, before.indexOf(line) + 1));
  }

  if (added.length) {
    const ecommerce = {
      currency,
      value: Number(added.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2)),
      items: added,
    };
    if (coupon) ecommerce.coupon = coupon;
    pushEvent('add_to_cart', ecommerce);
  }

  if (removed.length) {
    const ecommerce = {
      currency,
      value: Number(removed.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2)),
      items: removed,
    };
    if (coupon) ecommerce.coupon = coupon;
    pushEvent('remove_from_cart', ecommerce);
  }
}

function couponFromCart(cart) {
  const discountCode = cart?.discount_codes?.find((entry) => entry.applicable !== false)?.code;
  const application = cart?.cart_level_discount_applications?.[0] || cart?.discount_applications?.[0];
  return cleanString(discountCode || application?.title);
}

/** @param {CustomEvent} event */
function handleCartUpdate(event) {
  const nextCart = event.detail?.resource;
  if (event.detail?.data?.didError || !Array.isArray(nextCart?.items)) return;
  const nextCoupon = couponFromCart(nextCart);
  trackCartDiff(cartState, nextCart.items, nextCoupon);
  cartState = nextCart.items;
  activeCoupon = nextCoupon;
}

function trackViewCart() {
  if (!cartState.length) return;
  const items = cartState.map((line, index) => itemFromCartLine(line, Number(line.quantity || 1), index + 1));
  const ecommerce = {
    currency,
    value: Number(items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2)),
    items,
  };
  if (activeCoupon) ecommerce.coupon = activeCoupon;
  pushEvent('view_cart', ecommerce);
}

function bindCartDrawers() {
  for (const drawer of document.querySelectorAll('cart-drawer-component')) {
    if (!(drawer instanceof HTMLElement) || boundCartDrawers.has(drawer)) continue;
    boundCartDrawers.add(drawer);
    drawer.addEventListener('dialog:open', trackViewCart);
  }
}

/** @param {Record<string, any>} event */
export function validateEvent(event) {
  const errors = [];
  if (!TRACKED_EVENTS.has(event?.event)) errors.push('Unknown or missing event name');
  if (!event?.ecommerce || typeof event.ecommerce !== 'object') return [...errors, 'Missing ecommerce object'];
  if (!Array.isArray(event.ecommerce.items) || event.ecommerce.items.length === 0) return [...errors, 'Missing ecommerce.items'];
  if ('value' in event.ecommerce && typeof event.ecommerce.value !== 'number') errors.push('ecommerce.value must be a number');

  event.ecommerce.items.forEach((item, index) => {
    const prefix = `items[${index}]`;
    if (!cleanString(item.item_name)) errors.push(`${prefix}.item_name is missing`);
    if (!cleanString(item.item_id)) errors.push(`${prefix}.item_id is missing`);
    if (typeof item.price !== 'number' || !Number.isFinite(item.price)) errors.push(`${prefix}.price must be a number`);
    if (!['true', 'false'].includes(item.in_stoke)) errors.push(`${prefix}.in_stoke must be "true" or "false"`);
    if (!cleanString(item.item_category)) errors.push(`${prefix}.item_category is missing`);
    if (typeof item.quantity !== 'number' || item.quantity <= 0) errors.push(`${prefix}.quantity must be a positive number`);
    if ('discount' in item && (typeof item.discount !== 'number' || item.discount <= 0)) errors.push(`${prefix}.discount must be a positive number when present`);
  });

  return errors;
}

function init() {
  window.dataLayer = window.dataLayer || [];
  if (window.Shopify?.designMode || ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    console.log('[AMOI dataLayer] initialized');
  }
  document.addEventListener('click', handleProductSelection, false);
  document.addEventListener('click', handleWishlist, true);
  document.addEventListener('cart:update', handleCartUpdate, true);
  document.addEventListener('discount:update', handleCartUpdate, true);

  observeProductCards();
  bindCartDrawers();
  trackViewItem();
  if (window.location.pathname === '/cart') trackViewCart();

  const mutationObserver = new MutationObserver(() => {
    observeProductCards();
    bindCartDrawers();
  });
  mutationObserver.observe(document.body, { childList: true, subtree: true });

  window.AmoiEcommerce = {
    getEvents: () => window.dataLayer.filter((entry) => TRACKED_EVENTS.has(entry?.event)),
    inspect: () => window.dataLayer.filter((entry) => TRACKED_EVENTS.has(entry?.event)).map((entry) => ({
      event: entry.event,
      errors: validateEvent(entry),
      ecommerce: entry.ecommerce,
    })),
    pushEvent,
    validateEvent,
  };
}

init();
